import {isDeliverableMailbox} from "../../core/validation/mailbox";
import {utf8ByteLength} from "../../core/validation/utf8ByteLength";
import type {GroupResponse} from "../../contacts/groups/GroupApi";
import type {PaidPlanId} from "../../pricing/planCatalogue";
import {exceedsEveryPlan, recoveryDays} from "../../pricing/planEntitlements";
// The recovery entitlement predicate is DELIBERATELY imported from the transfers feature rather than
// re-declared here (api#443): `transferMutations` already owns it for the transfer list's recover
// action, and one capability answered by two functions is exactly how a row ends up armable in the
// composer but unusable in the list.
import {canRecoverExpiredTransfers} from "../../transfers/transferMutations";
import {
	canUseDownloadNotifications, canUseTransferOptions, canUseZeroKnowledge, isOverUploadCap, Mode, Plan,
	recipientLimit,
} from "../gating";
import type {OverLimitPricing} from "../overLimit/overLimitOffer";
import type {SenderBlocker} from "../../sender/senderChoice";

/**
 * Pure, framework-free logic behind the create-transfer form (ui#49). Kept out of the hook and the
 * dumb view so the behavior invariants — the submit label/disabled matrix, recipient limiting and
 * the delivery mapping — stay unit-testable without rendering anything (epic #26).
 */

/**
 * How the transfer reaches the recipient. Maps to `TransferType` on the upload contract:
 * a plain share `link`, or a `recipients` send that notifies direct emails and/or saved groups
 * (both can be present — the send path calls /email and /group as needed, ui#210).
 */
export type Delivery = "link" | "recipients";

/**
 * One unified recipients field (ui#210): direct email chips AND selected group chips coexist in a
 * single selection. Direct emails go to POST /email; the groups' ids go to POST /group (the server
 * expands current persisted membership). The group `contacts` are advisory snapshots used only for
 * the combined client-side count/cap UX — transport sends only each group's id.
 */
export interface RecipientSelection {
	emails: string[];
	groups: GroupResponse[];
}

/** The paid transfer-option switches surfaced in the actionbar "⋯" popover. */
export type OptionKey = "password" | "dlimit" | "zeroKnowledge" | "notify" | "recovery";

export interface OptionDef {
	key: OptionKey;
	/**
	 * Mono glyph rendered in the `.opt-ico` tile (design uses two-letter mono badges). The row title
	 * and sub-line are localized in the React layer from `sender.options[key]` (ui#121).
	 */
	icon: string;
	/**
	 * Capability gate for this option — free users see it PRO-locked and get the upgrade nudge on
	 * toggle. Each option carries its own gate so they can diverge (password/dlimit share
	 * `canUseTransferOptions`; Zero-Knowledge uses `canUseZeroKnowledge` — ui#65; download alerts
	 * use `canUseDownloadNotifications` — api#92).
	 */
	canUse: (plan: Plan) => boolean;
	/**
	 * Which tier the locked pill advertises: the lowest plan whose gate passes. Password/dlimit/alerts
	 * unlock at Basic; Zero-Knowledge (api#643) and expiry recovery (api#443) unlock at Premium. The
	 * tier metadata is kept beside each capability gate so its advertised upgrade can be checked
	 * against the catalogue (ui#659) — this value is HAND-WRITTEN, so an entitlement that moves in the
	 * api moves here too or the locked row sells an upgrade that does not unlock it.
	 *
	 * <p>Typed as a {@link PaidPlanId} rather than a free string so it can ALSO be fed to the
	 * entitlement selectors: hint copy that quotes a number ("we keep them for N more days") must
	 * quote the tier that unlocks the option, which is exactly this value — and it stays correct for
	 * a locked viewer, whose own tier grants nothing.
	 */
	unlockTier: PaidPlanId;
}

/**
 * The pill text for an unlock tier. A lookup rather than a ternary because a ternary can only ever
 * express two tiers, and silently mislabelled the third the moment one was added (api#443 put a
 * PREMIUM-only row next to the BASIC and PLUS ones).
 *
 * <p>Keyed over EVERY paid tier, so it serves the option rows and the expiry rungs alike: the rungs
 * had their own fixed "BASIC" string until ui#1203, which is how the PREMIUM-only indefinite rung
 * came to advertise a tier that would not have unlocked it.
 */
export function pillLabelFor(unlockTier: PaidPlanId, labels: Record<PaidPlanId, string>): string {
	return labels[unlockTier];
}

/** The catalog copy a locked upsell badge is built from — the pill per tier, and its aria gloss. */
export interface LockedTierCopy {
	pills: Record<PaidPlanId, string>;
	lockedAria: (plan: string) => string;
}

/** What a locked rung's gold badge SAYS: the visible tier name, and what it means to a screen reader. */
export interface LockedBadge {
	pillLabel: string;
	aria: string;
}

/**
 * The badge for a rung locked behind `unlockTier` (ui#1203).
 *
 * <p>Both strings come out of ONE tier lookup, so the visible badge and the announcement cannot name
 * different plans. That is the whole point of composing them together rather than at the render
 * site: the defect this replaced was a fixed "BASIC" passed to each of them independently.
 */
export function lockedBadgeFor(unlockTier: PaidPlanId, copy: LockedTierCopy): LockedBadge {
	const pillLabel = pillLabelFor(unlockTier, copy.pills);
	return {pillLabel, aria: copy.lockedAria(pillLabel)};
}

/** Catalog shape for the option hints — the localized copy {@link optionHintFor} formats (api#443). */
export interface OptionHintCopy {
	zeroKnowledge: string;
	recovery: (days: number) => string;
}

/**
 * The plain-language hint for an option row, or `undefined` for the rows that need none (api#443).
 *
 * <p>The recovery hint quotes a real number of retained days, and it reads it from the catalogue for
 * the row's own {@link OptionDef.unlockTier} rather than for the VIEWER's plan. That distinction is
 * the whole reason this is a function: a locked row advertises what the upgrade buys, and a free
 * viewer's own `recoveryDays` is 0 — quoting that would render "we keep them for 0 more days" on the
 * very row asking them to upgrade.
 */
export function optionHintFor(def: OptionDef, copy: OptionHintCopy): string | undefined {
	switch (def.key) {
		case "zeroKnowledge":
			return copy.zeroKnowledge;
		case "recovery":
			return copy.recovery(recoveryDays(def.unlockTier));
		default:
			return undefined;
	}
}

/**
 * The paid options offered in the actionbar popover, in display order. "Email to people" is NOT
 * here — it is the free recipients field the sheet renders above these, and since ui#890 dropped the
 * delivery tabs it is not a switch at all: filling it adds an email send to the link.
 * Custom link + Scheduled send are NOT here — they have no backend and are rendered from
 * {@link COMING_SOON_OPTIONS} as inert rows instead (ui#216). Every row here is always rendered — the
 * E2EE rollout flag that used to hide Zero-Knowledge was retired in ui#306 — and entitlement shows as
 * a locked pill rather than an absent row.
 * "notify" is the per-transfer download-alerts opt-in (api#92): armed → `notifyOnDownload` on the
 * create request; the api emails the sender when a counted download starts (at most ten per transfer
 * per UTC day), without waiting for it to finish.
 * "recovery" is the per-transfer expiry-recovery opt-in (api#443): armed → `recoveryOptIn` on the
 * create request, which is what makes the api retain the bytes past the link's expiry. It is OFF by
 * default on purpose — the retained storage is billed for the whole window, so it is bought only
 * where it was asked for.
 * Row copy (label/desc) is localized from `sender.options[key]` in {@link ../useCreateForm} (ui#121).
 */
export const TRANSFER_OPTIONS: OptionDef[] = [
	{key: "password", icon: "PW", canUse: canUseTransferOptions, unlockTier: "basic"},
	{key: "dlimit", icon: "DL", canUse: canUseTransferOptions, unlockTier: "basic"},
	{key: "zeroKnowledge", icon: "ZK", canUse: canUseZeroKnowledge, unlockTier: "premium"},
	{key: "notify", icon: "DN", canUse: canUseDownloadNotifications, unlockTier: "basic"},
	{key: "recovery", icon: "RC", canUse: canRecoverExpiredTransfers, unlockTier: "premium"},
];

/**
 * Whether the unified field carries anyone to send to — one email chip or one group chip is enough.
 * Answered here once so the form and {@link ../CreateFlow}'s post-authentication boundary cannot
 * disagree about what "has recipients" means.
 */
export function hasRecipients(selection: RecipientSelection | null): boolean {
	return selection !== null && (selection.emails.length > 0 || selection.groups.length > 0);
}

/**
 * Recipient delivery needs server-managed keys. Suspend, rather than erase, the sender's ZK choice
 * while chips are present, so removing the last chip restores it. The row and payload read this
 * same result; an armed-looking switch must never produce an escrow transfer.
 *
 * A new link to stored files (kitecorp/transferify-ui#3405) is never Zero-Knowledge either: their keys are
 * already the server's, which is what lets the api link them again.
 */
export function resolveZeroKnowledge(plan: Plan, requested: boolean, selection: RecipientSelection | null,
									 storedFiles = false) {
	const disabled = storedFiles || hasRecipients(selection);
	const on = requested && canUseZeroKnowledge(plan) && !disabled;
	return {on, disabled, keyMode: on ? "zeroKnowledge" as const : "escrow" as const};
}

/**
 * The mode a send goes out in: a new link to stored files (kitecorp/transferify-ui#3405) is always hosted,
 * because its files are already stored and there is nothing to seed from this browser.
 */
export function storedFilesMode(mode: Mode, storedFiles: boolean): Mode {
	return storedFiles ? "upload" : mode;
}

/**
 * How this transfer is delivered, DERIVED from the recipients the sender collected (ui#890).
 *
 * There is no delivery mode any more — the tabs that asked for one are gone, because both of their
 * answers produced a link. The link is ALWAYS produced and recipients are purely additive: an empty
 * field is a plain share `link`, and any chip makes it a `recipients` send, which the api emails IN
 * ADDITION to returning that same link. A recipients send can carry direct emails, saved groups, or
 * both — the split happens at send time, not here (ui#210).
 *
 * Deriving this rather than storing a picked mode is what makes the two impossible to disagree: no
 * state can claim "link" while chips sit in the field, and none can claim "recipients" with nothing
 * to send to.
 */
export function deliveryOf(selection: RecipientSelection | null): Delivery {
	return hasRecipients(selection) ? "recipients" : "link";
}

/** A recipients send calls authenticated account endpoints (/email, /group); a plain link does not. */
export function isRecipientDelivery(delivery: Delivery): boolean {
	return delivery !== "link";
}

/** Case-insensitive account/sender identity comparison after trimming browser input whitespace. */
export function sameEmailAddress(senderEmail: string, accountEmail: string | null | undefined): boolean {
	return accountEmail != null && senderEmail.trim().toLowerCase() === accountEmail.trim().toLowerCase();
}

/** Authenticated identity is canonical; anonymous visitors keep the address they are entering. */
export function canonicalSenderEmail(typedEmail: string, accountEmail: string | null | undefined): string {
	const canonical = accountEmail?.trim() ?? "";
	return canonical === "" ? typedEmail : canonical;
}

/**
 * Whether the selected route uploads bytes to hosted storage. Email delivery always uploads first,
 * even when the Instant tab is selected; only an Instant link is true peer-to-peer delivery.
 */
export function usesHostedUpload(mode: Mode, delivery: Delivery): boolean {
	return mode === "upload" || delivery !== "link";
}

/**
 * Which over-limit rule prices this send (api kitecorp/transferify-api-service#1039, ui#1719): the same
 * condition {@link usesHostedUpload} answers, said as MONEY.
 *
 * It is the same condition on purpose rather than a second reading of the composer's state — an email
 * send is hosted even from the Instant tab, and a pricing rule that disagreed with the route about that
 * would quote the flat figure for a transfer we then store.
 */
export function overLimitPricingOf(mode: Mode, delivery: Delivery): OverLimitPricing {
	return usesHostedUpload(mode, delivery) ? "hosted" : "instant";
}

/**
 * The total the sender DECLARES, whichever way the transfer is delivered.
 *
 * Sums the ORIGINAL browser-selected sizes, so encryption chunks and ciphertext overhead never consume
 * quota — and never inflate a QUOTE either, which is why the over-limit offer is asked for this figure
 * rather than for anything measured later in the pipeline (ui#1454).
 *
 * Separate from {@link hostedSelectionBytes} because the two answer different questions and stopped
 * having the same answer at ui#1719: hosted bytes are what STORAGE will hold (zero for a peer-to-peer
 * link), while this is what the transfer WEIGHS — and an instant transfer weighs its files even though
 * it uploads none of them, which is exactly what the api quotes and enforces against.
 */
export function declaredSelectionBytes(files: ReadonlyArray<{size: number}>): number {
	return files.reduce((sum, file) => sum + file.size, 0);
}

/**
 * The bytes a hosted route would upload, or 0 when the route uploads nothing (a true Instant link).
 */
export function hostedSelectionBytes(
	mode: Mode,
	delivery: Delivery,
	files: ReadonlyArray<{size: number}>,
): number {
	if (!usesHostedUpload(mode, delivery)) return 0;
	return declaredSelectionBytes(files);
}

/**
 * Final hosted-route allowance decision over the original browser-selected byte sizes. Keeping this
 * composition pure lets both the form and the post-authentication upload boundary enforce one rule.
 *
 * STILL A REAL BOUNDARY after ui#1454, and deliberately so: the over-cap state stopped being a dead end
 * and became a PRICED path, which is not the same as the rule going away. This answers "is the
 * selection over the viewer's own plan cap" and nothing else; what that MEANS — pay, upgrade, or refuse
 * — is {@link ../overLimit/overLimitOffer.resolveOverLimit}'s answer, and an unpaid over-cap submit is
 * still refused there.
 */
export function isHostedSelectionOverCap(
	mode: Mode,
	delivery: Delivery,
	plan: Plan,
	files: ReadonlyArray<{size: number}>,
): boolean {
	if (!usesHostedUpload(mode, delivery)) return false;
	return isOverUploadCap(plan, hostedSelectionBytes(mode, delivery, files));
}

/**
 * The hosted selection is above the largest plan's cap (kitecorp/transferify-api-service#3005): the api sells no
 * plan and no one-off for it and refuses it for every tier, so the composer says so instead of nudging to an upgrade.
 *
 * Only a route that stores bytes can be above it. A true Instant link stores none, so it has no ceiling:
 * {@link hostedSelectionBytes} is 0 there, which no plan cap is below, so this states the rule rather than adding one.
 */
export function isHostedSelectionAboveEveryPlan(
	mode: Mode,
	delivery: Delivery,
	files: ReadonlyArray<{size: number}>,
): boolean {
	return exceedsEveryPlan(hostedSelectionBytes(mode, delivery, files));
}

/**
 * Whether a send must be refused at the composer because no plan holds its size (kitecorp/transferify-api-service#3005):
 * a hosted selection above the largest plan, unless the send names a purchase that already covers it (the strip's `paid`
 * rung). The strip holds Send shut on this rung, so this is the last check before the upload starts, for a send that
 * reaches the flow some other way.
 */
export function refusesAboveEveryPlan(
	{mode, delivery, overLimitAllowanceId}: {mode: Mode; delivery: Delivery; overLimitAllowanceId?: string},
	files: ReadonlyArray<{size: number}>,
): boolean {
	return isHostedSelectionAboveEveryPlan(mode, delivery, files) && overLimitAllowanceId === undefined;
}

/** True when the sender field holds an address the SMTP transport will accept (shared grammar, ui#362). */
export function isValidSender(email: string): boolean {
	return isDeliverableMailbox(email.trim());
}

/**
 * The create-side download-password bounds, mirroring the api's `TransferProtection`
 * (kitecorp/transferify-api-service#183 / #184; the minimum was lowered from 8 to 4 by owner decision on
 * 2026-08-30, kitecorp/transferify-api-service#1015). Fewer than the minimum is refused as not worth
 * calling a password; more than 72 UTF-8 BYTES is refused because BCrypt hashes at most 72 bytes.
 * Characters are counted as UTF-16 code units, exactly as Java's `String.length()` counts them for
 * `@Size`, so both sides agree on a diacritic (1) and on an emoji (2). `createFormLogic.test.ts` pins
 * both numbers as literals so a change on either side is a red test rather than a silent drift.
 */
export const MIN_TRANSFER_PASSWORD_LENGTH = 4;
export const MAX_TRANSFER_PASSWORD_BYTES = 72;

/** Why an ARMED download password cannot be sent as it stands. */
export type TransferPasswordIssue = "empty" | "tooShort" | "tooLong";

/**
 * Validates an armed download password against the api's bounds, or null when it can be sent. Only
 * consulted while the password switch is on: an unarmed form carries no password and has nothing to
 * validate. `empty` is kept apart from `tooShort` so the field can withhold its error styling until
 * the sender has typed something, while the submit rule treats both as not sendable — an armed
 * switch with nothing typed used to ship the transfer UNPROTECTED without a word, and a 1–3 character
 * password used to fail the whole send at upload init with the generic "upload failed" copy
 * (kitecorp/transferify-ui#384).
 */
export function transferPasswordIssue(password: string): TransferPasswordIssue | null {
	if (password.length === 0) return "empty";
	if (password.length < MIN_TRANSFER_PASSWORD_LENGTH) return "tooShort";
	if (utf8ByteLength(password) > MAX_TRANSFER_PASSWORD_BYTES) return "tooLong";
	return null;
}

/** Which `sender.optionsSheet.passwordFeedback` line the field shows, and whether as an error. */
export type TransferPasswordFeedbackKey = "bounds" | "tooShort" | "tooLong";

export interface TransferPasswordFeedback {
	key: TransferPasswordFeedbackKey;
	error: boolean;
}

/**
 * The field's helper line for an issue: the bounds themselves while the field is untouched or valid,
 * the specific refusal — styled as an error — once the sender has typed something outside them.
 */
export function transferPasswordFeedback(issue: TransferPasswordIssue | null): TransferPasswordFeedback {
	if (issue === "tooShort" || issue === "tooLong") return {key: issue, error: true};
	return {key: "bounds", error: false};
}

export interface SubmitInput {
	fileCount: number;
	mode: Mode;
	/** The delivery {@link deliveryOf} derived from the collected recipients (ui#890). */
	delivery: Delivery;
	senderValid: boolean;
	/**
	 * The selection's SIZE stops this send.
	 *
	 * Named for the verdict rather than for the cap since ui#1454, because the two stopped being the
	 * same thing: a FREE sender over the cap who holds a purchase from before the one-off stopped being sold is over
	 * the cap and must still submit. A field still called `overCap` here would have read as "blocked" at every call
	 * site while meaning "large", which is exactly how a paid sender ends up with a dead Send button.
	 * {@link ../overLimit/overLimitOffer.overLimitBlocksSend} is what answers it.
	 */
	sizeBlocked: boolean;
	/**
	 * Whether the collected recipients pass the plan's cap ({@link validateRecipientSelection}).
	 * Consulted ONLY for a recipients send: an empty field is a link, never an invalid send (ui#890).
	 */
	recipientsValid: boolean;
	/**
	 * Whether the armed download password clears the api's bounds ({@link transferPasswordIssue});
	 * true when the switch is off. Stopped here, at the composer, because the api's 400 for it reaches
	 * the sender only as the generic "upload failed" copy (kitecorp/transferify-ui#384).
	 */
	passwordValid: boolean;
	/** A P2P seed / upload is already in flight. */
	isSeeding: boolean;
	/** A sign-in request or confirmation, or a Send's wait for the restored session (ui#2088), is in flight. */
	authInFlight: boolean;
	/**
	 * Why the transfer cannot go out as the company it is sent as yet ({@link SenderBlocker}), or null/absent for a
	 * person and a company that can go (kitecorp/transferify-ui#3186).
	 */
	senderBlock?: SenderBlocker | null;
}

/**
 * Which submit state the form is in — the React layer maps this to the localized
 * `sender.submit.*` label (ui#121), keeping the copy out of this pure module.
 *  - `noFiles`     → nothing selected yet
 *  - `noSender`    → sender email missing/invalid
 *  - `badPassword` → an armed download password is outside the api's bounds
 *  - `addCompanyName` / `checkCompanyId` → sent as a company that has no name, or a tax ID the api would refuse
 *    (kitecorp/transferify-ui#3186, `SenderBlocker`)
 *  - `instant`     → submittable, instant/P2P mode
 *  - `upload`      → submittable, hosted upload mode
 */
export type SubmitStatus = "noFiles" | "noSender" | "badPassword" | "addCompanyName" | "checkCompanyId" | "instant"
	| "upload";

export interface SubmitState {
	status: SubmitStatus;
	disabled: boolean;
}

/**
 * The submit button's status + disabled state (design `submitLabel`/`submitDisabled`):
 *  - no files      → disabled `noFiles`
 *  - no sender     → disabled `noSender`
 *  - bad password  → disabled `badPassword` (an armed password outside the api's bounds, ui#384)
 *  - no company name → disabled `addCompanyName` (sent as a company that has no name, kitecorp/transferify-ui#3186)
 *  - bad tax ID    → disabled `checkCompanyId` (sent as a company whose tax ID the api would refuse)
 *  - otherwise     → `instant` / `upload` by mode, disabled while size-blocked or already seeding.
 *
 * The sender email is MANDATORY for every transfer regardless of delivery (ui#71) — a plain link and
 * a recipients send both require it (owner decision; supersedes the ui#49 "only when email on" rule).
 *
 * The recipient cap is enforced here rather than folded into the caller's `sizeBlocked` (ui#890). They
 * are different refusals, and passing one under the other's name leaves the disabled state unable to
 * say which rule stopped it — the hook renders a different surface for each, so the two would have
 * drifted the moment anything else consulted the folded value.
 */
export function resolveSubmit({
	fileCount, mode, delivery, senderValid, sizeBlocked, recipientsValid, passwordValid, isSeeding, authInFlight,
	senderBlock = null,
}: SubmitInput): SubmitState {
	if (fileCount === 0) return {status: "noFiles", disabled: true};
	if (!senderValid) return {status: "noSender", disabled: true};
	if (!passwordValid) return {status: "badPassword", disabled: true};
	if (senderBlock !== null) return {status: senderBlock === "companyName" ? "addCompanyName" : "checkCompanyId", disabled: true};
	const status = usesHostedUpload(mode, delivery) ? "upload" : "instant";
	const recipientsBlocked = isRecipientDelivery(delivery) && !recipientsValid;
	return {status, disabled: sizeBlocked || recipientsBlocked || isSeeding || authInFlight};
}

export interface RecipientSelectionState {
	count: number;
	limit: number;
	overLimit: boolean;
}

export interface RecipientValidation {
	count: number;
	limit: number;
	valid: boolean;
}

function normalizedRecipientEmail(raw: string): string | null {
	const email = raw.trim().replace(/,$/, "").trim().toLowerCase();
	return isDeliverableMailbox(email) ? email : null;
}

/**
 * Trims, lower-cases and de-duplicates a batch of raw addresses case-insensitively, dropping any that
 * fail validation and tolerating a trailing comma (the "type-and-comma" entry gesture). Exported for
 * the success-screen "email instead" flow (ui#51) which normalizes its own recipient list.
 */
export function normalizeEmails(emails: readonly string[]): string[] {
	const unique = new Set<string>();
	for (const raw of emails) {
		const email = normalizedRecipientEmail(raw);
		if (email !== null) unique.add(email);
	}
	return [...unique];
}

/** Normalizes the unified field's two inputs into a de-duplicated selection (ui#210). */
export function recipientSelection(
	emails: readonly string[],
	groups: readonly GroupResponse[],
): RecipientSelection {
	const uniqueGroups = new Map(groups.map(group => [group.id, group]));
	return {emails: normalizeEmails(emails), groups: [...uniqueGroups.values()]};
}

/**
 * The combined case-insensitive unique union of the direct emails AND every selected group's expanded
 * membership — used only for client count/cap guidance. Direct chips and group chips count against one
 * shared recipient cap (ui#210): a person addressed both directly and via a group is counted once.
 */
export function recipientEmails(selection: {
	emails: readonly string[];
	groups: readonly GroupResponse[];
}): string[] {
	return normalizeEmails([
		...selection.emails,
		...selection.groups.flatMap(group => group.contacts.map(contact => contact.email)),
	]);
}

export function recipientSelectionState(selection: RecipientSelection, plan: Plan): RecipientSelectionState {
	const count = recipientEmails(selection).length;
	const limit = recipientLimit(plan);
	return {count, limit, overLimit: count > limit};
}

/** Shared by form admission and CreateFlow's final post-authentication action boundary. */
export function validateRecipientSelection(selection: RecipientSelection, plan: Plan): RecipientValidation {
	const {count, limit, overLimit} = recipientSelectionState(selection, plan);
	return {count, limit, valid: count > 0 && !overLimit};
}

/** Why the success screen's "email instead" send was refused — the hook maps it to localized copy. */
export type EmailInsteadRejection = "missing" | "overLimit";

export interface EmailInsteadSendInput {
	/** Recipients this transfer has ALREADY reached — the initial list plus every prior send. */
	knownEmails: readonly string[];
	/** The raw addresses currently typed into the recipients field. */
	newEmails: readonly string[];
	/** A send is already in flight. */
	sending: boolean;
	plan: Plan;
}

export interface EmailInsteadSendVerdict {
	/** True when there is at least one recipient and no send in flight. */
	canSend: boolean;
	/** Set when the send must be refused; null when it may proceed. */
	rejection: EmailInsteadRejection | null;
	/** Exactly what `POST /email` carries; the server derives sender identity from the account. */
	payload: {toEmails: string[]};
	/** The cumulative union to remember as {@link EmailInsteadSendInput.knownEmails} once the send succeeds. */
	cumulativeEmails: string[];
}

/**
 * The success screen's "email instead" send rule (ui#51, extracted in ui#223).
 *
 * The recipient cap is CUMULATIVE, not per-send: the new recipients are unioned with the ones this
 * transfer has already reached ({@link EmailInsteadSendInput.knownEmails}) BEFORE the plan cap is
 * measured, so a Free user cannot bypass their cap by sending twice. The caller must feed
 * {@link EmailInsteadSendVerdict.cumulativeEmails} back as `knownEmails` after a successful send —
 * that feedback is what makes the union grow across repeated sends.
 *
 * `canSend` (the button's enabled state) and `rejection` (the send-time refusal) deliberately share
 * ONE recipient-presence predicate but are NOT the same test: only `canSend` considers `sending`. A
 * send already in flight disables the button without turning an otherwise-valid send into a
 * `missing` rejection.
 *
 * Note `rejection` is `overLimit` when the union is EMPTY too — {@link validateRecipientSelection}
 * requires `count > 0`, so a field holding only unparseable addresses is refused with the cap
 * message. Preserved from the pre-extraction hook rather than "fixed" (ui#223 is a refactor).
 */
export function resolveEmailInsteadSend({
	knownEmails, newEmails, sending, plan,
}: EmailInsteadSendInput): EmailInsteadSendVerdict {
	// Deliberately NOT the module's `hasRecipients`: this asks whether the user TYPED anything at all
	// (so a field of unparseable addresses is "missing", not "empty"), not whether a selection carries
	// chips. Two different questions — the local name says which one this is.
	const hasNewRecipients = newEmails.length > 0;
	const normalizedNewRecipients = normalizeEmails(newEmails);
	const cumulative = recipientSelection([...knownEmails, ...normalizedNewRecipients], []);
	const overLimit = !validateRecipientSelection(cumulative, plan).valid;
	return {
		canSend: !sending && hasNewRecipients,
		rejection: !hasNewRecipients ? "missing" : overLimit ? "overLimit" : null,
		payload: {toEmails: normalizedNewRecipients},
		cumulativeEmails: cumulative.emails,
	};
}

export interface DirectRecipientUpdate {
	accepted: boolean;
	recipients: string[];
}

/**
 * Folds a batch of addresses (a typed chip or the emails of the contacts a user ticked in the picker,
 * ui#166) into the existing email chips, normalized + de-duplicated case-insensitively. The candidate
 * union is measured against the plan's cap TOGETHER with the already-selected {@link groups} (ui#210:
 * emails and groups share one recipient limit), then admitted or rejected atomically — never truncated.
 */
export function tryMergeRecipients(
	existing: string[],
	incoming: string[],
	groups: readonly GroupResponse[],
	plan: Plan,
): DirectRecipientUpdate {
	const emails = normalizeEmails([...existing, ...incoming]);
	const combined = recipientEmails({emails, groups}).length;
	if (combined > recipientLimit(plan)) {
		return {accepted: false, recipients: existing};
	}
	return {accepted: true, recipients: emails};
}

/** Why a group replacement was refused — the view maps it to specific guidance (ui#210). */
export type GroupRejection = "empty" | "overLimit";

export interface GroupRecipientUpdate {
	accepted: boolean;
	groups: GroupResponse[];
	/** Set only when {@link accepted} is false, so the view can pick the right message. */
	reason?: GroupRejection;
}

/**
 * Atomic replacement used when the group picker confirms its full controlled selection. Rejects a
 * selection whose groups expand to no members at all (`empty`), or whose combined union with the
 * already-typed {@link emails} would exceed the plan's shared recipient cap (`overLimit`) — the direct
 * chips are never dropped to make room (ui#210).
 */
export function replaceSelectedGroups(
	existing: GroupResponse[],
	incoming: readonly GroupResponse[],
	emails: readonly string[],
	plan: Plan,
): GroupRecipientUpdate {
	const groups = recipientSelection([], incoming).groups;
	if (recipientEmails({emails: [], groups}).length === 0) {
		return {accepted: false, groups: existing, reason: "empty"};
	}
	if (recipientEmails({emails, groups}).length > recipientLimit(plan)) {
		return {accepted: false, groups: existing, reason: "overLimit"};
	}
	return {accepted: true, groups};
}
